// ─── Generic preset bar (quick-filter groups, save/load, column visibility, export) ──
//
// Generalized from `TransactionTable/TransactionTablePresetBar.tsx`. Quick-
// filter "phase" stat cards become config-driven `GroupConfig` stat-card rows
// (any table can declare one or more group axes — Transaction's single
// `phase` axis, Property's `division` + `managementType` axes). Column
// labels/options all come from the consumer's `ColumnMeta`/`GroupConfig`,
// nothing table-specific is hardcoded here.
//
// Note: this v1 covers the desktop-oriented feature set (stat cards,
// save/load modal, column visibility + reorder, search, export, clear
// filters). TransactionTablePresetBar's mobile long-press drawer variant is
// not reproduced here — a follow-up pass can add responsive polish once this
// abstraction has been validated against a second real consumer.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  CloseButton,
  Divider,
  Drawer,
  Fieldset,
  Group,
  Modal,
  Popover,
  ScrollArea,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
  useMantineColorScheme
} from '@mantine/core';
import {
  IconArrowAutofitWidth,
  IconBookmark,
  IconBookmarkFilled,
  IconCircleX,
  IconFileSpreadsheet,
  IconFilter,
  IconGripVertical,
  IconMinus,
  IconPlus,
  IconSearch,
  IconTableExport,
  IconTableOptions,
  IconTrash,
  IconWorldSearch
} from '@tabler/icons-react';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { AppPermissions, useMobileBreakpoint } from '@repo/utils';
import {
  ColumnConfig,
  ColumnMetaMap,
  GridPreset,
  GroupConfig,
  isEnumFilterValue,
  isRangeFilterValue,
  LAST_PRESET_ID
} from './types';
import { filterGridItems, filterGridItemsByGroups } from './gridEngine';
import { PermissionGate } from '../auth/PermissionGate';

// ─── Shared bordered-group-box styling ───────────────────────────────────────
//
// Used by any section that groups a row of chips/cards under a titled,
// bordered box (quick-filter group axes like Division/Management Type, and
// the Saved Views section) — keeps the legend's typography consistent with
// the rest of the toolbar's small-uppercase-dimmed label convention rather
// than Mantine's bulkier `Fieldset` default.
/**
 * Shared bordered-group-box styling used for any section that groups a row of chips/cards under a titled, bordered fieldset — keeps the legend typography consistent with the toolbar's small-uppercase-dimmed label convention.
 */
const FIELDSET_STYLES = {
  root: { paddingTop: 6 },
  legend: {
    fontSize: 'var(--mantine-font-size-xs)',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: 'var(--mantine-color-dimmed)',
    padding: '0 4px',
  },
};

// ─── Group stat card ─────────────────────────────────────────────────────────

/**
 * Props for the GroupStatCard component that renders a single quick-filter stat card.
 */
interface GroupStatCardProps {
  label: string;
  count: number;
  active: boolean;
  anyActiveInGroup: boolean;
  isSoleActive: boolean;
  color?: string;
  onClick: () => void;
  onAdd: () => void;
}

/**
 * Renders a single quick-filter stat card showing a row count and label for one group option (e.g. one Division value), highlighting when selected and exposing an add/remove control for multi-select.
 *
 * @param label - Label shown below the count.
 * @param count - Number of rows matching this option.
 * @param active - Whether this option is currently selected.
 * @param anyActiveInGroup - Whether any option in this group axis is currently selected.
 * @param isSoleActive - Whether this is the only currently selected option in the group.
 * @param color - Mantine color name for the card's accent.
 * @param onClick - Called when the card body is clicked (select/toggle-off if sole active).
 * @param onAdd - Called when the add/remove badge is clicked (multi-select toggle).
 */
function GroupStatCard({ label, count, active, anyActiveInGroup, isSoleActive, color = 'blue', onClick, onAdd }: GroupStatCardProps) {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';

  return (
    <Box style={{ position: 'relative', display: 'inline-block', userSelect: 'none' }}>
      <UnstyledButton onClick={onClick}>
        <Box
          px={14}
          py={8}
          style={{
            borderRadius: 8,
            border: `2px solid ${active ? `var(--mantine-color-${color}-5)` : 'var(--mantine-color-default-border)'}`,
            backgroundColor: active
              ? dark
                ? `var(--mantine-color-${color}-9)`
                : `var(--mantine-color-${color}-0)`
              : dark
                ? 'var(--mantine-color-dark-6)'
                : 'var(--mantine-color-gray-0)',
            transition: 'all 120ms ease',
            minWidth: 90,
            textAlign: 'center',
            opacity: active ? 1 : 0.55,
          }}
        >
          <Text fz={22} fw={700} lh={1.1} c={active ? color : 'dimmed'}>
            {count}
          </Text>
          <Text fz={11} fw={500} tt='uppercase' c={active ? `${color}.7` : 'dimmed'} style={{ letterSpacing: '0.04em', marginTop: 2 }}>
            {label}
          </Text>
        </Box>
      </UnstyledButton>

      {anyActiveInGroup && !isSoleActive && (
        <Tooltip label={active ? `Remove ${label} from filter` : `Add ${label} to filter`} withArrow>
          <ActionIcon
            size={20}
            variant='filled'
            color={color}
            radius='xl'
            style={{ position: 'absolute', top: -6, right: -6 }}
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
          >
            {active ? <IconMinus size={12} stroke={3} /> : <IconPlus size={12} stroke={3} />}
          </ActionIcon>
        </Tooltip>
      )}
    </Box>
  );
}

// ─── Action cards (Clear filters / Table options) ────────────────────────────
//
// Same visual weight/shape as the stat cards (GroupStatCard/PresetStatCard) —
// a bordered rounded box with a centered icon + uppercase label — so the
// toolbar actions read as first-class controls rather than tiny icon buttons.
// Ported from TransactionTablePresetBar's `ClearFiltersCard`/`TableOptionsCard`.

/**
 * Renders a bordered, icon-plus-label action card (e.g. "Clear filters" or "Table options") matching the visual weight of the group/preset stat cards.
 *
 * @param icon - Icon rendered above the label.
 * @param label - Label text shown below the icon.
 * @param tooltip - Tooltip text shown on hover.
 * @param accent - Mantine color name for the border/background/text accent; omit for a neutral style.
 * @param onClick - Called when the card is clicked.
 * @param disabled - Disables the card when true.
 */
function ActionCard({
  icon,
  label,
  tooltip,
  accent,
  onClick,
  disabled
}: {
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  /** Mantine color name for the border/text/bg tint. Omit for neutral (default border, dimmed). */
  accent?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';

  const border = accent ? `2px solid var(--mantine-color-${accent}-5)` : '2px solid var(--mantine-color-default-border)';
  const bg = accent
    ? dark
      ? `var(--mantine-color-${accent}-9)`
      : `var(--mantine-color-${accent}-0)`
    : dark
      ? 'var(--mantine-color-dark-6)'
      : 'var(--mantine-color-gray-0)';
  const labelColor = accent ? (dark ? `${accent}.3` : `${accent}.7`) : 'dimmed';

  return (
    <Tooltip label={tooltip} withArrow>
      <UnstyledButton onClick={onClick} disabled={disabled}>
        <Box
          px={14}
          py={8}
          style={{
            borderRadius: 8,
            border,
            backgroundColor: bg,
            transition: 'all 120ms ease',
            minWidth: 90,
            textAlign: 'center',
            userSelect: 'none'
          }}
        >
          <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 28 }}>
            {icon}
          </Box>
          <Text
            fz={11}
            fw={500}
            tt='uppercase'
            c={labelColor}
            style={{ letterSpacing: '0.04em', marginTop: 2 }}
          >
            {label}
          </Text>
        </Box>
      </UnstyledButton>
    </Tooltip>
  );
}

// ─── Group row ───────────────────────────────────────────────────────────────

/**
 * Props for the GroupStatRow component that renders one row of quick-filter stat cards for a single group axis.
 */
interface GroupStatRowProps<TRow, K extends string> {
  group: GroupConfig<K>;
  rows: TRow[];
  columnMeta: ColumnMetaMap<TRow, K>;
  groupFilters: Record<string, string[]>;
  onChange: (groupId: string, selected: string[]) => void;
}

/**
 * Renders one row of quick-filter stat cards for a single group axis (e.g. Division or Management Type), inside a labeled bordered fieldset.
 *
 * @param group - The group axis configuration (field, label, options) to render.
 * @param rows - The current (group-filtered) row set, used to compute per-option counts.
 * @param columnMeta - Column metadata map, used to resolve each row's value for this group's field.
 * @param groupFilters - Currently selected option values per group id.
 * @param onChange - Called with the group id and the updated selection when an option is toggled.
 */
function GroupStatRow<TRow, K extends string>({ group, rows, columnMeta, groupFilters, onChange }: GroupStatRowProps<TRow, K>) {
  const meta = columnMeta[group.field];
  const selected = groupFilters[group.id] ?? [];

  const counts: Record<string, number> = {};
  for (const row of rows) {
    const raw = meta?.getValue ? meta.getValue(row) : meta?.dataKey ? (row as Record<string, unknown>)[meta.dataKey] : undefined;
    const value = raw != null ? String(raw) : '';
    if (!value) continue;
    counts[value] = (counts[value] ?? 0) + 1;
  }

  const anyActive = selected.length > 0;

  // Each group axis (e.g. Division, Management Type) gets its own bordered
  // box with the group name as a fieldset legend above its chips, rather
  // than a label to the left of them — trades a little vertical space for a
  // lot of horizontal space, and multiple group boxes still flow/wrap
  // together via the caller's wrapping <Group>.
  return (
    <Fieldset
      legend={group.label}
      variant='default'
      radius='sm'
      p='xs'
      styles={FIELDSET_STYLES}
    >
      <Group gap='xs' wrap='wrap'>
        {group.options.map((opt) => {
          const active = selected.includes(opt.value);
          const isSoleActive = active && selected.length === 1;
          return (
            <GroupStatCard
              key={opt.value}
              label={opt.label}
              count={counts[opt.value] ?? 0}
              active={active}
              anyActiveInGroup={anyActive}
              isSoleActive={isSoleActive}
              color={opt.color}
              onClick={() => onChange(group.id, isSoleActive ? [] : [opt.value])}
              onAdd={() => onChange(group.id, active ? selected.filter((v) => v !== opt.value) : [...selected, opt.value])}
            />
          );
        })}
      </Group>
    </Fieldset>
  );
}

// ─── Long-press hook (mobile preset-card actions) ────────────────────────────
//
// Ported from TransactionTablePresetBar.tsx — distinguishes a tap (select the
// preset) from a long-press (open the actions popover), since mobile has no
// hover state to reveal the desktop micro-buttons.

/**
 * Returns touch event handlers and a ref to distinguish a long-press gesture from a tap — scheduled `onLongPress` fires only after `delay` ms, cancelled on touch end/move.
 *
 * @param onLongPress - Callback invoked when a long-press gesture is confirmed.
 * @param delay - Milliseconds before the long-press fires. Defaults to 400.
 * @returns An object with `onTouchStart`, `onTouchEnd`, `onTouchMove` handlers and a `didLongPress` ref.
 *
 * @example
 * const { onTouchStart, didLongPress } = useLongPress(() => alert('long press'), 500)
 */
function useLongPress(onLongPress: () => void, delay = 400) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = useRef(false);

  const start = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault(); // prevent text selection on long-press
      didLongPress.current = false;
      timerRef.current = setTimeout(() => {
        didLongPress.current = true;
        onLongPress();
      }, delay);
    },
    [onLongPress, delay],
  );

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return {
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchMove: cancel,
    /** True if the most recent touch sequence triggered a long-press */
    didLongPress,
  };
}

// ─── Preset stat card ────────────────────────────────────────────────────────
//
// Ported from TransactionTablePresetBar.tsx's `PresetStatCard` — a card grid
// of saved views (name + live match count) replacing a plain dropdown list.
// Desktop reveals bookmark (set default) / trash (delete) micro-buttons on
// hover; mobile has no hover, so a long-press opens a small actions popover
// instead. The "isDirty"/"unsaved base" dashed-border treatment from the
// original IS reproduced here (see `isUnsavedBase` below) — `useGridPresets`
// forks edits into a separate `__last__` scratch state tracked against a
// `basePresetId`, same as the original's model.

/**
 * Props for the PresetStatCard component that renders a saved-view card with hover/long-press actions.
 */
interface PresetStatCardProps {
  label: string;
  count: number;
  active: boolean;
  isDefault: boolean;
  /**
   * True when this preset is the base of the current (dirty) unsaved
   * working state — i.e. `isDirty && preset.id === lastBasePresetId`. Shown
   * as a dashed border + trailing asterisk, distinct from `active`'s solid
   * border (which won't be true for any named preset while on the `__last__`
   * working state).
   */
  isUnsavedBase: boolean;
  onSelect: () => void;
  onDelete?: () => void;
  onSetDefault: () => void;
}

/**
 * Renders a saved-view card showing its name and live match count, with desktop hover micro-buttons or a mobile long-press popover for setting it as default or deleting it.
 *
 * @param label - Preset display name.
 * @param count - Live match count for this preset's filters against the full row set.
 * @param active - Whether this preset is currently active.
 * @param isDefault - Whether this preset is the user's default view.
 * @param isUnsavedBase - Whether this preset is the base of the current dirty/unsaved working state.
 * @param onSelect - Called when the card is selected (tap, or click on desktop).
 * @param onDelete - Called to delete this preset; omit to hide the delete action.
 * @param onSetDefault - Called to set this preset as the default.
 */
function PresetStatCard({ label, count, active, isDefault, isUnsavedBase, onSelect, onDelete, onSetDefault }: PresetStatCardProps) {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const isMobileCard = useMobileBreakpoint();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const longPress = useLongPress(() => setPopoverOpen(true), 400);

  const borderStyle = isUnsavedBase
    ? '2px dashed var(--mantine-color-teal-5)'
    : active
      ? '2px solid var(--mantine-color-teal-5)'
      : '2px solid var(--mantine-color-default-border)';

  const cardContent = (
    <Box
      px={14}
      py={8}
      style={{
        borderRadius: 8,
        border: borderStyle,
        backgroundColor: active
          ? dark
            ? 'var(--mantine-color-teal-9)'
            : 'var(--mantine-color-teal-0)'
          : dark
            ? 'var(--mantine-color-dark-6)'
            : 'var(--mantine-color-gray-0)',
        transition: 'all 120ms ease',
        minWidth: 90,
        textAlign: 'center',
        opacity: active || isUnsavedBase ? 1 : 0.6,
        userSelect: 'none',
        paddingBottom: 10,
      }}
    >
      <Text fz={22} fw={700} lh={1.1} c={active ? 'teal' : 'dimmed'}>
        {count}
      </Text>
      <Tooltip label={label} disabled={!label} withArrow>
        <Text
          fz={11}
          fw={500}
          tt='uppercase'
          c={active ? (dark ? 'teal.3' : 'teal.7') : 'dimmed'}
          style={{ letterSpacing: '0.04em', marginTop: 2, maxWidth: 100 }}
          lineClamp={1}
          aria-label={label}
        >
          {label}
          {isUnsavedBase ? ' *' : ''}
        </Text>
      </Tooltip>
    </Box>
  );

  return (
    <Popover opened={popoverOpen} onChange={setPopoverOpen} position='top' withArrow shadow='md'>
      <Popover.Target>
        <Box style={{ position: 'relative', WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }} className='preset-card-wrap'>
          <UnstyledButton
            onClick={() => {
              // If a long-press just fired, don't also select.
              if (longPress.didLongPress.current) {
                longPress.didLongPress.current = false;
                return;
              }
              onSelect();
            }}
            {...(isMobileCard
              ? { onTouchStart: longPress.onTouchStart, onTouchEnd: longPress.onTouchEnd, onTouchMove: longPress.onTouchMove }
              : {})}
          >
            {cardContent}
          </UnstyledButton>

          {/* Desktop: micro action buttons on hover */}
          {!isMobileCard && (
            <Group gap={2} style={{ position: 'absolute', bottom: 2, right: 4, opacity: 0 }} className='preset-card-actions'>
              <Tooltip label={isDefault ? 'Default preset' : 'Set as default'} withArrow>
                <ActionIcon
                  size={14}
                  variant='transparent'
                  c={isDefault ? (dark ? 'teal.3' : 'teal.6') : 'dimmed'}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetDefault();
                  }}
                >
                  {isDefault ? <IconBookmarkFilled size={11} /> : <IconBookmark size={11} />}
                </ActionIcon>
              </Tooltip>
              {onDelete && (
                <Tooltip label='Delete preset' withArrow>
                  <ActionIcon
                    size={14}
                    variant='transparent'
                    c='dimmed'
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                  >
                    <IconTrash size={11} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          )}
        </Box>
      </Popover.Target>

      <Popover.Dropdown p={8}>
        <Stack gap={6}>
          <Text fz='xs' fw={600} c='dimmed' tt='uppercase' style={{ letterSpacing: '0.04em' }}>
            {label}
          </Text>
          <Button
            variant={isDefault ? 'light' : 'subtle'}
            color='teal'
            size='md'
            fullWidth
            justify='flex-start'
            leftSection={isDefault ? <IconBookmarkFilled size={20} /> : <IconBookmark size={20} />}
            onClick={() => {
              onSetDefault();
              setPopoverOpen(false);
            }}
          >
            {isDefault ? 'Default view' : 'Set as default'}
          </Button>
          {onDelete && (
            <Button
              variant='subtle'
              color='red'
              size='md'
              fullWidth
              justify='flex-start'
              leftSection={<IconTrash size={20} />}
              onClick={() => {
                onDelete();
                setPopoverOpen(false);
              }}
            >
              Delete preset
            </Button>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

// ─── Column visibility + reorder list ────────────────────────────────────────

/**
 * Props for the ColumnVisibilityList component that renders a drag-and-drop reorderable list of columns with visibility switches.
 */
interface ColumnVisibilityListProps<K extends string> {
  columns: ColumnConfig<K>[];
  labelFor: (key: K) => string;
  columnOrder?: K[];
  onChange: (columns: ColumnConfig<K>[]) => void;
}

/**
 * Renders a drag-and-drop reorderable list of columns with visibility switches, used inside the Table Options modal.
 *
 * @param columns - The columns to list, in their persisted order.
 * @param labelFor - Resolves a column key to its display label.
 * @param onChange - Called with the updated column list after a visibility toggle or drag reorder.
 */
function ColumnVisibilityList<K extends string>({ columns, labelFor, columnOrder, onChange }: ColumnVisibilityListProps<K>) {
  const orderIndex = columnOrder ? new Map(columnOrder.map((key, index) => [key, index])) : undefined;
  const ordered = [...columns].sort((a, b) => orderIndex
    ? (orderIndex.get(a.key) ?? Number.MAX_SAFE_INTEGER) - (orderIndex.get(b.key) ?? Number.MAX_SAFE_INTEGER)
    : a.order - b.order);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = Array.from(ordered);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    onChange(reordered.map((c, i) => ({ ...c, order: i })));
  };

  const toggleVisible = (key: K) => {
    onChange(columns.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)));
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId='datagrid-columns'>
        {(provided) => (
          <Stack gap={2} ref={provided.innerRef} {...provided.droppableProps}>
            {ordered.map((col, index) => (
              <Draggable key={col.key} draggableId={String(col.key)} index={index}>
                {(dragProvided) => (
                  <Group
                    gap='xs'
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    wrap='nowrap'
                    py={4}
                    style={{ ...dragProvided.draggableProps.style }}
                  >
                    <Box {...dragProvided.dragHandleProps} style={{ cursor: 'grab', display: 'flex' }}>
                      <IconGripVertical size={14} color='var(--mantine-color-dimmed)' />
                    </Box>
                    <Switch checked={col.visible} onChange={() => toggleVisible(col.key)} size='sm' />
                    <Text fz='sm' style={{ flex: 1 }}>
                      {labelFor(col.key)}
                    </Text>
                  </Group>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Stack>
        )}
      </Droppable>
    </DragDropContext>
  );
}

// ─── Grouped-by reorder strip ────────────────────────────────────────────────

// ─── Main preset bar ──────────────────────────────────────────────────────────

/**
 * Props for the DataGridPresetBar component that renders the full toolbar above a DataGrid table.
 */
export interface DataGridPresetBarProps<TRow, K extends string> {
  rows: TRow[];
  columnMeta: ColumnMetaMap<TRow, K>;
  groupConfigs: GroupConfig<K>[];
  preset: GridPreset<K>;
  presets: GridPreset<K>[];
  onPatchPreset: (partial: Partial<GridPreset<K>>) => void;
  onSaveAsNewPreset: (name: string) => void;
  onSelectPreset: (presetId: string) => void;
  onDeletePreset: (presetId: string) => void;
  onSetDefaultPreset: (presetId: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  /** Whether export controls are shown at all — gate this at the caller for permission/UX reasons. */
  canExport?: boolean;
  onExportCsv?: () => void;
  onExportXlsx?: () => void;
  /** Permission keys required to see the Export section — user needs at least ONE (OR logic). Defaults to `[AppPermissions.prismInvestment.transactionExport]`. */
  exportPermissions?: string[];
  resultCount: number;
  /** True when the current working state has diverged from `lastBasePresetId` — see `useGridPresets`. */
  isDirty: boolean;
  /** Which named preset id the current working state was forked from — see `useGridPresets`. */
  lastBasePresetId: string;
  /** Overwrites `lastBasePresetId`'s saved preset with the current working state — see `useGridPresets`. */
  onSaveToBasePreset: () => void;
  /** Resets any user-resized column widths back to their defaults. When omitted, the reset button is hidden. */
  onResetColumnWidths?: () => void;
  /** Optional canonical order for the column options list. */
  columnOrder?: K[];
  /** Uses a compact search/filter bar with the remaining controls in a drawer. */
  compact?: boolean;
}

/**
 * Renders the full toolbar above a DataGrid table: quick-filter group stat cards, search, active-filter badges with per-filter clear, saved-view preset cards (save/select/default/delete), and a Table Options modal for CSV/XLSX export and column visibility/reordering.
 */
export function DataGridPresetBar<TRow, K extends string>({
  rows,
  columnMeta,
  groupConfigs,
  preset,
  presets,
  onPatchPreset,
  onSaveAsNewPreset,
  onSelectPreset,
  onDeletePreset,
  onSetDefaultPreset,
  searchQuery,
  onSearchChange,
  canExport = true,
  onExportCsv,
  onExportXlsx,
  exportPermissions,
  resultCount,
  isDirty,
  lastBasePresetId,
  onSaveToBasePreset,
  onResetColumnWidths,
  columnOrder,
  compact = false,
}: DataGridPresetBarProps<TRow, K>) {
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const isMobile = useMobileBreakpoint();
  const [saveOpen, setSaveOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');

  const namedPresets = useMemo(() => presets.filter((p) => p.id !== LAST_PRESET_ID), [presets]);
  const basePreset = namedPresets.find((p) => p.id === lastBasePresetId);
  const canSaveToBase = lastBasePresetId !== 'default';

  // Live match count per saved view — re-applies that preset's own
  // filters/groupFilters (not the currently active ones) against the full
  // unfiltered row set, mirroring TransactionTablePresetBar's
  // `presetsWithCounts`. Ignores free-text search, same as the original.
  const groupFieldById = useMemo(
    () => Object.fromEntries(groupConfigs.map((g) => [g.id, g.field])) as Record<string, K>,
    [groupConfigs],
  );
  const presetCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of namedPresets) {
      const groupFiltered = filterGridItemsByGroups(rows, p.groupFilters, groupFieldById, columnMeta);
      counts[p.id] = filterGridItems(groupFiltered, p.filters, columnMeta).length;
    }
    return counts;
  }, [namedPresets, rows, groupFieldById, columnMeta]);

  // Only ever show/toggle columns that exist in the CURRENT columnMeta — a
  // persisted preset may still reference a key that's gated for this user
  // (e.g. MarketValue.Read was revoked after the preset was saved) or that
  // no longer exists at all. Filtering here (not just at the table-render
  // level) keeps gated/removed columns from ever appearing in the column
  // visibility list, matching the "entirely absent, not just hidden"
  // requirement for permission-gated columns.
  const availableColumns = preset.columns.filter((c) => c.key in columnMeta);

  const handleGroupChange = (groupId: string, selected: string[]) => {
    onPatchPreset({ groupFilters: { ...preset.groupFilters, [groupId]: selected } });
  };

  // Column/group filters active, IGNORING the free-text search. Drives the
  // "Search all" affordance: when the user is searching but has narrowing
  // filters on, "Search all" drops those filters (keeping the search term) so
  // the search runs across everything.
  const hasColumnOrGroupFilters =
    Object.values(preset.filters).some((v) => (Array.isArray(v) ? v.length > 0 : v.min != null || v.max != null)) ||
    Object.values(preset.groupFilters).some((v) => v.length > 0);

  const hasActiveFilters = hasColumnOrGroupFilters || !!searchQuery;

  const clearAllFilters = () => {
    onPatchPreset({ filters: {}, groupFilters: {} });
    onSearchChange('');
  };

  // Clears column/group filters but KEEPS the search query.
  const searchAll = () => {
    onPatchPreset({ filters: {}, groupFilters: {} });
  };

  const compactContent = (
    <>
      <Group gap='xs' wrap='nowrap'>
        <TextInput
          placeholder='Search…'
          leftSection={<IconSearch size={14} />}
          size='sm'
          value={searchQuery}
          onChange={(e) => onSearchChange(e.currentTarget.value)}
          rightSection={
            searchQuery ? <CloseButton size='sm' aria-label='Clear search' title='Clear search' onClick={() => onSearchChange('')} /> : undefined
          }
          rightSectionPointerEvents='auto'
          style={{ flex: 1 }}
        />
        <ActionIcon
          variant={hasActiveFilters ? 'filled' : 'default'}
          color={hasActiveFilters ? 'blue' : undefined}
          size='lg'
          aria-label='Open filters and views'
          onClick={() => setFilterDrawerOpen(true)}
        >
          <IconFilter size={18} />
        </ActionIcon>
      </Group>

      <Drawer
        opened={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        position='left'
        size='min(360px, 90vw)'
        title={<Text fw={600}>Filters &amp; Views</Text>}
      >
        <ScrollArea h='calc(100vh - 90px)' offsetScrollbars>
          <Stack gap='lg'>
            <Fieldset legend='Filters' variant='default' radius='sm' p='xs' styles={FIELDSET_STYLES}>
              <Stack gap='xs'>
                {groupConfigs.map((group) => (
                  <GroupStatRow
                    key={group.id}
                    group={group}
                    rows={rows}
                    columnMeta={columnMeta}
                    groupFilters={preset.groupFilters}
                    onChange={handleGroupChange}
                  />
                ))}
              </Stack>
            </Fieldset>

            <Fieldset legend='Saved Views' variant='default' radius='sm' p='xs' styles={FIELDSET_STYLES}>
              <Group gap={6} wrap='wrap' align='center'>
                {namedPresets.map((p) => (
                  <PresetStatCard
                    key={p.id}
                    label={p.name}
                    count={presetCounts[p.id] ?? 0}
                    active={preset.id === p.id}
                    isDefault={p.isDefault}
                    isUnsavedBase={isDirty && p.id === lastBasePresetId}
                    onSelect={() => {
                      onSelectPreset(p.id);
                      setFilterDrawerOpen(false);
                    }}
                    onSetDefault={() => onSetDefaultPreset(p.id)}
                    onDelete={p.id !== 'default' ? () => onDeletePreset(p.id) : undefined}
                  />
                ))}
                <Popover opened={saveOpen} onChange={setSaveOpen} position='bottom-start' withArrow shadow='md'>
                  <Popover.Target>
                    <Tooltip label='Save current view as new'>
                      <ActionIcon variant='default' size='lg' radius='xl' onClick={() => setSaveOpen((o) => !o)}>
                        <IconPlus size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Group gap={4}>
                      <TextInput
                        size='xs'
                        placeholder='View name…'
                        value={newPresetName}
                        onChange={(e) => setNewPresetName(e.currentTarget.value)}
                        style={{ flex: 1 }}
                      />
                      <ActionIcon
                        variant='light'
                        disabled={!newPresetName.trim()}
                        onClick={() => {
                          onSaveAsNewPreset(newPresetName.trim());
                          setNewPresetName('');
                          setSaveOpen(false);
                        }}
                      >
                        <IconBookmark size={14} />
                      </ActionIcon>
                    </Group>
                  </Popover.Dropdown>
                </Popover>
              </Group>
            </Fieldset>

            {hasActiveFilters && (
              <Button variant='light' color='red' leftSection={<IconCircleX size={16} />} fullWidth onClick={clearAllFilters}>
                Clear all filters
              </Button>
            )}

            <Button
              variant='default'
              leftSection={<IconTableOptions size={16} />}
              fullWidth
              justify='flex-start'
              onClick={() => {
                setFilterDrawerOpen(false);
                setOptionsOpen(true);
              }}
            >
              Table options (columns and export)
            </Button>
          </Stack>
        </ScrollArea>
      </Drawer>
      <Modal opened={optionsOpen} onClose={() => setOptionsOpen(false)} title='Table Options' size='sm'>
        <Stack gap='md'>
          {canExport && (
            <PermissionGate permissions={exportPermissions ?? [AppPermissions.prismInvestment.transactionExport]} inline>
              <Group gap='xs'>
                <Button size='xs' variant='default' leftSection={<IconTableExport size={14} />} onClick={onExportCsv}>CSV</Button>
                <Button size='xs' variant='default' leftSection={<IconFileSpreadsheet size={14} />} onClick={onExportXlsx}>XLSX</Button>
              </Group>
            </PermissionGate>
          )}
          <Stack gap={4}>
            <Group justify='space-between' align='center'>
              <Text fz='xs' c='dimmed' fw={600} tt='uppercase'>Columns</Text>
              {onResetColumnWidths && (
                <Button size='compact-xs' variant='subtle' color='gray' leftSection={<IconArrowAutofitWidth size={14} />} onClick={onResetColumnWidths}>
                  Reset widths
                </Button>
              )}
            </Group>
            <ScrollArea.Autosize mah={320}>
              <ColumnVisibilityList
                columns={availableColumns}
                labelFor={(key) => columnMeta[key]?.label ?? String(key)}
                columnOrder={columnOrder}
                onChange={(columns) => onPatchPreset({ columns })}
              />
            </ScrollArea.Autosize>
          </Stack>
          <Badge variant='light' color='gray' size='sm' style={{ alignSelf: 'flex-start' }}>
            {availableColumns.filter((c) => c.visible).length} of {availableColumns.length} visible
          </Badge>
        </Stack>
      </Modal>
    </>
  );

  // One badge per active column filter (enum/range) and per active
  // group-quick-filter axis, each with its own close icon to clear just that
  // filter — shown next to the result count so the user can see/undo what's
  // currently narrowing the row set without opening a column header popover.
  const activeFilterBadges = useMemo(() => {
    const badges: { id: string; label: string; valueText: string; onClear: () => void }[] = [];

    for (const [key, value] of Object.entries(preset.filters)) {
      const meta = columnMeta[key as K];
      if (!meta) continue;

      if (isEnumFilterValue(value)) {
        if (value.length === 0) continue;
        const valueText = value.length > 3 ? `${value.slice(0, 3).join(', ')} +${value.length - 3} more` : value.join(', ');
        badges.push({
          id: `col:${key}`,
          label: meta.label,
          valueText,
          onClear: () => {
            const rest = { ...preset.filters };
            delete rest[key];
            onPatchPreset({ filters: rest });
          },
        });
      } else if (isRangeFilterValue(value)) {
        if (value.min == null && value.max == null) continue;
        const fmt = meta.formatAggregate ?? ((n: number) => n.toLocaleString());
        const valueText =
          value.min != null && value.max != null
            ? `${fmt(value.min)} – ${fmt(value.max)}`
            : value.min != null
              ? `≥ ${fmt(value.min)}`
              : `≤ ${fmt(value.max as number)}`;
        badges.push({
          id: `col:${key}`,
          label: meta.label,
          valueText,
          onClear: () => {
            const rest = { ...preset.filters };
            delete rest[key];
            onPatchPreset({ filters: rest });
          },
        });
      }
    }

    for (const group of groupConfigs) {
      const selected = preset.groupFilters[group.id] ?? [];
      if (selected.length === 0) continue;
      const optionLabels = selected.map((v) => group.options.find((o) => o.value === v)?.label ?? v);
      const valueText =
        optionLabels.length > 3 ? `${optionLabels.slice(0, 3).join(', ')} +${optionLabels.length - 3} more` : optionLabels.join(', ');
      badges.push({
        id: `group:${group.id}`,
        label: group.label,
        valueText,
        onClear: () => onPatchPreset({ groupFilters: { ...preset.groupFilters, [group.id]: [] } }),
      });
    }

    return badges;
  }, [preset.filters, preset.groupFilters, columnMeta, groupConfigs, onPatchPreset]);

  return compact || isMobile ? compactContent : (
    <Stack gap='xs'>
      <style>{`.preset-card-wrap:hover .preset-card-actions { opacity: 1 !important; }`}</style>

      <Group justify='space-between' align='flex-start' wrap='wrap'>
        <Group gap='xs' wrap='wrap' align='flex-start'>
          {groupConfigs.map((group) => (
            <GroupStatRow
              key={group.id}
              group={group}
              rows={rows}
              columnMeta={columnMeta}
              groupFilters={preset.groupFilters}
              onChange={handleGroupChange}
            />
          ))}
        </Group>

        <Group gap='xs' align='flex-end' wrap='nowrap'>
          {/* Title-less fieldset grouping the Clear + Options action cards,
              matching the Search/Saved Views fieldset framing. */}
          <Fieldset variant='default' radius='sm' p='xs' styles={FIELDSET_STYLES} mt={10} mih={93}>
            <Group gap='xs' align='flex-end' wrap='nowrap'>
              {hasActiveFilters ? (
                <ActionCard
                  icon={<IconCircleX size={22} color='var(--mantine-color-red-5)' />}
                  label='Clear'
                  tooltip='Clear all active filters'
                  accent='red'
                  onClick={clearAllFilters}
                />
              ):<Box h={67} w={90}  />
              }

              <ActionCard
                icon={<IconTableOptions size={22} color='var(--mantine-color-dimmed)' />}
                label='Options'
                tooltip='Table options'
                onClick={() => setOptionsOpen(true)}
              />
            </Group>
          </Fieldset>

          {/* Search in a `Fieldset` (legend "Search") matching the "Saved
              Views" fieldset, placed after the action cards so it holds a
              stable position instead of jumping as the Clear card
              appears/disappears. */}
          <Fieldset h={98} legend='Search' variant='default' radius='sm' p='xs' styles={FIELDSET_STYLES}>
            <TextInput
              placeholder='Search…'
              leftSection={<IconSearch size={14} />}
              size='sm'
              value={searchQuery}
              onChange={(e) => onSearchChange(e.currentTarget.value)}
              rightSection={
                searchQuery ? <CloseButton size='sm' aria-label='Clear search' title='Clear search' onClick={() => onSearchChange('')} /> : undefined
              }
              rightSectionPointerEvents='auto'
            />
            {searchQuery && hasColumnOrGroupFilters && (
              <Button
                size='compact-xs'
                variant='light'
                color='blue'
                fullWidth
                mt={5}
                leftSection={<IconWorldSearch size={14} />}
                onClick={searchAll}
              >
                Search all
              </Button>
            )}
          </Fieldset>
        </Group>
      </Group>

      <Fieldset legend='Saved Views' variant='default' radius='sm' p='xs' styles={FIELDSET_STYLES}>
        <Group gap={6} wrap='wrap' align='center'>
          {namedPresets.map((p) => (
            <PresetStatCard
              key={p.id}
              label={p.name}
              count={presetCounts[p.id] ?? 0}
              active={preset.id === p.id}
              isDefault={p.isDefault}
              isUnsavedBase={isDirty && p.id === lastBasePresetId}
              onSelect={() => onSelectPreset(p.id)}
              onSetDefault={() => onSetDefaultPreset(p.id)}
              onDelete={p.id !== 'default' ? () => onDeletePreset(p.id) : undefined}
            />
          ))}

          <Popover opened={saveOpen} onChange={setSaveOpen} position='bottom-start' withArrow shadow='md'>
            <Popover.Target>
              <Tooltip label='Save current view as new'>
                <ActionIcon variant='default' size='lg' radius='xl' onClick={() => setSaveOpen((o) => !o)}>
                  <IconPlus size={16} />
                </ActionIcon>
              </Tooltip>
            </Popover.Target>
            <Popover.Dropdown>
              <Group gap={4}>
                <TextInput
                  size='xs'
                  placeholder='View name…'
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e.currentTarget.value)}
                  style={{ flex: 1 }}
                  data-autofocus
                />
                <ActionIcon
                  variant='light'
                  disabled={!newPresetName.trim()}
                  onClick={() => {
                    onSaveAsNewPreset(newPresetName.trim());
                    setNewPresetName('');
                    setSaveOpen(false);
                  }}
                >
                  <IconBookmark size={14} />
                </ActionIcon>
              </Group>
            </Popover.Dropdown>
          </Popover>
        </Group>
      </Fieldset>

      <Group justify='space-between'>
        <Group gap={6} align='center' wrap='wrap'>
          <Text fz='xs' c='dimmed'>
            {resultCount.toLocaleString()} {resultCount === 1 ? 'result' : 'results'}
          </Text>
          {activeFilterBadges.length > 0 && (
            <Text fz='xs' c='dimmed' fw={600}>
              Filtered by:
            </Text>
          )}
          {activeFilterBadges.map((b) => (
            <Badge
              key={b.id}
              variant='light'
              color='blue'
              size='sm'
              rightSection={
                <CloseButton size={12} variant='transparent' c='inherit' onClick={b.onClear} aria-label={`Clear ${b.label} filter`} />
              }
            >
              {b.label}: {b.valueText}
            </Badge>
          ))}
        </Group>
      </Group>

      <Modal opened={optionsOpen} onClose={() => setOptionsOpen(false)} title='Table Options' size='sm'>
        <Stack gap='md'>
          {isDirty && (
            <>
              <Stack gap={4}>
                <Text fz='xs' c='dimmed' fw={600} tt='uppercase'>
                  Saved View
                </Text>
                <Tooltip label={!canSaveToBase ? 'Default view cannot be overwritten — use "+" to save as new' : undefined} disabled={canSaveToBase}>
                  <Button
                    variant='light'
                    color='teal'
                    fullWidth
                    justify='flex-start'
                    leftSection={<IconBookmark size={16} />}
                    disabled={!canSaveToBase}
                    onClick={() => {
                      onSaveToBasePreset();
                      setOptionsOpen(false);
                    }}
                  >
                    Save to &ldquo;{basePreset?.name ?? 'view'}&rdquo;
                  </Button>
                </Tooltip>
              </Stack>

              <Divider />
            </>
          )}

          {canExport && (
            <PermissionGate permissions={exportPermissions ?? [AppPermissions.prismInvestment.transactionExport]} inline>
              <Stack gap={4}>
                <Text fz='xs' c='dimmed' fw={600} tt='uppercase'>
                  Export
                </Text>
                <Group gap='xs'>
                  <Button size='xs' variant='default' leftSection={<IconTableExport size={14} />} onClick={onExportCsv}>
                    CSV
                  </Button>
                  <Button size='xs' variant='default' leftSection={<IconFileSpreadsheet size={14} />} onClick={onExportXlsx}>
                    XLSX
                  </Button>
                </Group>
              </Stack>
            </PermissionGate>
          )}

          <Divider />

          <Stack gap={4}>
            <Group justify='space-between' align='center'>
              <Text fz='xs' c='dimmed' fw={600} tt='uppercase'>
                Columns
              </Text>
              {onResetColumnWidths && (
                <Tooltip label='Reset column widths to fit their content' withArrow>
                  <Button
                    size='compact-xs'
                    variant='subtle'
                    color='gray'
                    leftSection={<IconArrowAutofitWidth size={14} />}
                    onClick={onResetColumnWidths}
                  >
                    Reset widths
                  </Button>
                </Tooltip>
              )}
            </Group>
            <ScrollArea.Autosize mah={320}>
              <ColumnVisibilityList
                columns={availableColumns}
                labelFor={(key) => columnMeta[key]?.label ?? String(key)}
                columnOrder={columnOrder}
                onChange={(columns) => onPatchPreset({ columns })}
              />
            </ScrollArea.Autosize>
          </Stack>

          <Badge variant='light' color='gray' size='sm' style={{ alignSelf: 'flex-start' }}>
            {availableColumns.filter((c) => c.visible).length} of {availableColumns.length} visible
          </Badge>
        </Stack>
      </Modal>
    </Stack>
  );
}

export default DataGridPresetBar;
